import { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';

interface SettingsState {
  site_title: string;
  site_description: string;
  properties_per_page: string;
  contact_email: string;
  contact_phone: string;
  contact_whatsapp: string;
  contact_address: string;
  instagram_url: string;
  facebook_url: string;
  youtube_url: string;
  whatsapp_url: string;
  logo_url: string;
  hero_image_url: string;
}

const defaultSettings: SettingsState = {
  site_title: 'Anchor Vault Estate',
  site_description: '',
  properties_per_page: '12',
  contact_email: 'info@anchorvaultestate.com',
  contact_phone: '+255658984616',
  contact_whatsapp: '+255658984616',
  contact_address: 'Paje, Zanzibar, Tanzania',
  instagram_url: '',
  facebook_url: '',
  youtube_url: '',
  whatsapp_url: '',
  logo_url: '',
  hero_image_url: '',
};

export default function AdminSettings() {
  const [settings, setSettings] = useState<SettingsState>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('site_settings_ave')
        .select('key, value');

      if (error) throw error;

      if (data && data.length > 0) {
        const settingsMap: Partial<SettingsState> = {};
        data.forEach((item) => {
          settingsMap[item.key as keyof SettingsState] = item.value;
        });
        setSettings({ ...defaultSettings, ...settingsMap });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load settings. Using defaults.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (keys: (keyof SettingsState)[]) => {
    try {
      setSaving(true);

      const updates = keys.map((key) => ({
        key,
        value: settings[key],
      }));

      for (const update of updates) {
        const { error } = await supabase
          .from('site_settings_ave')
          .upsert(update, { onConflict: 'key' });

        if (error) throw error;
      }

      toast({
        title: 'Success',
        description: 'Settings saved successfully.',
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save settings. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (key: keyof SettingsState, value: string) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Site Settings</h1>
          <p className="text-muted-foreground mt-2">
            Configure your website settings and preferences
          </p>
        </div>

        <Tabs defaultValue="general" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 lg:w-auto">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="contact">Contact</TabsTrigger>
            <TabsTrigger value="social">Social Media</TabsTrigger>
            <TabsTrigger value="branding">Branding</TabsTrigger>
          </TabsList>

          <TabsContent value="general">
            <Card>
              <CardHeader>
                <CardTitle>General Settings</CardTitle>
                <CardDescription>
                  Basic site configuration and preferences
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="site_title">Site Title</Label>
                  <Input
                    id="site_title"
                    value={settings.site_title}
                    onChange={(e) => handleChange('site_title', e.target.value)}
                    placeholder="Anchor Vault Estate"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="site_description">Site Description</Label>
                  <Textarea
                    id="site_description"
                    value={settings.site_description}
                    onChange={(e) => handleChange('site_description', e.target.value)}
                    placeholder="Enter site description"
                    rows={4}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="properties_per_page">Properties Per Page</Label>
                  <Input
                    id="properties_per_page"
                    type="number"
                    value={settings.properties_per_page}
                    onChange={(e) => handleChange('properties_per_page', e.target.value)}
                    placeholder="12"
                    min="1"
                  />
                </div>

                <Button
                  onClick={() => handleSave(['site_title', 'site_description', 'properties_per_page'])}
                  disabled={saving}
                  className="w-full sm:w-auto"
                >
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save General Settings
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="contact">
            <Card>
              <CardHeader>
                <CardTitle>Contact Information</CardTitle>
                <CardDescription>
                  Manage your contact details and address
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="contact_email">Contact Email</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    value={settings.contact_email}
                    onChange={(e) => handleChange('contact_email', e.target.value)}
                    placeholder="info@anchorvaultestate.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact_phone">Phone Number</Label>
                  <Input
                    id="contact_phone"
                    type="tel"
                    value={settings.contact_phone}
                    onChange={(e) => handleChange('contact_phone', e.target.value)}
                    placeholder="+255658984616"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact_whatsapp">WhatsApp Number</Label>
                  <Input
                    id="contact_whatsapp"
                    type="tel"
                    value={settings.contact_whatsapp}
                    onChange={(e) => handleChange('contact_whatsapp', e.target.value)}
                    placeholder="+255658984616"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact_address">Address</Label>
                  <Textarea
                    id="contact_address"
                    value={settings.contact_address}
                    onChange={(e) => handleChange('contact_address', e.target.value)}
                    placeholder="Paje, Zanzibar, Tanzania"
                    rows={3}
                  />
                </div>

                <Button
                  onClick={() => handleSave(['contact_email', 'contact_phone', 'contact_whatsapp', 'contact_address'])}
                  disabled={saving}
                  className="w-full sm:w-auto"
                >
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save Contact Settings
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="social">
            <Card>
              <CardHeader>
                <CardTitle>Social Media Links</CardTitle>
                <CardDescription>
                  Connect your social media profiles
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="instagram_url">Instagram URL</Label>
                  <Input
                    id="instagram_url"
                    type="url"
                    value={settings.instagram_url}
                    onChange={(e) => handleChange('instagram_url', e.target.value)}
                    placeholder="https://instagram.com/yourusername"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="facebook_url">Facebook URL</Label>
                  <Input
                    id="facebook_url"
                    type="url"
                    value={settings.facebook_url}
                    onChange={(e) => handleChange('facebook_url', e.target.value)}
                    placeholder="https://facebook.com/yourpage"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="youtube_url">YouTube URL</Label>
                  <Input
                    id="youtube_url"
                    type="url"
                    value={settings.youtube_url}
                    onChange={(e) => handleChange('youtube_url', e.target.value)}
                    placeholder="https://youtube.com/@yourchannel"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="whatsapp_url">WhatsApp URL</Label>
                  <Input
                    id="whatsapp_url"
                    type="url"
                    value={settings.whatsapp_url}
                    onChange={(e) => handleChange('whatsapp_url', e.target.value)}
                    placeholder="https://wa.me/255658984616"
                  />
                </div>

                <Button
                  onClick={() => handleSave(['instagram_url', 'facebook_url', 'youtube_url', 'whatsapp_url'])}
                  disabled={saving}
                  className="w-full sm:w-auto"
                >
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save Social Media Settings
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="branding">
            <Card>
              <CardHeader>
                <CardTitle>Branding Assets</CardTitle>
                <CardDescription>
                  Manage your logo and hero images
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="logo_url">Logo URL</Label>
                  <Input
                    id="logo_url"
                    type="url"
                    value={settings.logo_url}
                    onChange={(e) => handleChange('logo_url', e.target.value)}
                    placeholder="https://example.com/logo.png"
                  />
                  {settings.logo_url && (
                    <div className="mt-4 p-4 border border-border rounded-lg bg-muted/30">
                      <p className="text-sm text-muted-foreground mb-2">Logo Preview:</p>
                      <img
                        src={settings.logo_url}
                        alt="Logo preview"
                        className="max-h-24 object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="hero_image_url">Hero Image URL</Label>
                  <Input
                    id="hero_image_url"
                    type="url"
                    value={settings.hero_image_url}
                    onChange={(e) => handleChange('hero_image_url', e.target.value)}
                    placeholder="https://example.com/hero.jpg"
                  />
                  {settings.hero_image_url && (
                    <div className="mt-4 p-4 border border-border rounded-lg bg-muted/30">
                      <p className="text-sm text-muted-foreground mb-2">Hero Image Preview:</p>
                      <img
                        src={settings.hero_image_url}
                        alt="Hero image preview"
                        className="max-h-48 w-full object-cover rounded-lg"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                </div>

                <Button
                  onClick={() => handleSave(['logo_url', 'hero_image_url'])}
                  disabled={saving}
                  className="w-full sm:w-auto"
                >
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save Branding Settings
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}